import React, { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import AppLayout from "../AppLayout/AppLayout";
import { issuesRoutes } from "./development";

// Lazy-loaded route components
const Signup = lazy(() => import("../User/Signup"));
const SignIn = lazy(() => import("../User/SignIn"));
const Chat = lazy(() => import("../Chat/index"));
const AppHome = lazy(() => import("../AppHome/AppHome"));
const Community = lazy(() => import("../Community/Community"));
const Discussion = lazy(() => import("../Discussion/Discussion"));
const InvitationContainer = lazy(() => import("../Invitations/InvitationContainer"));
const People = lazy(() => import("../People/People"));
const CrossCommunity = lazy(() => import("../People/CrossCommunity"));
const Body = lazy(() => import("../Body/Body"));
const Invite = lazy(() => import("../Invitations/Invite"));
const Pay = lazy(() => import("../Pay/Pay"));
const ReviewPayments = lazy(() => import("../Payments/ReviewPayments"));
const Dues = lazy(() => import("../Dues/Dues"));
const AccountForm = lazy(() => import("../Account/AccountForm"));
const AccountDetails = lazy(() => import("../Account/AccountDetails"));
const FundRaiseDetails = lazy(() => import("../Account/FundRaiseDetails"));
const HowToUse = lazy(() => import("../HowToUse/HowToUse"));
const FeatureGuide = lazy(() => import("../HowToUse/FeatureGuide"));
const DemoPage = lazy(() => import("../HowToUse/ProductDemo").then(m => ({ default: m.DemoPage })));
const ResetData = lazy(() => import("../HowToUse/ResetData"));
const WorksAndExpenses = lazy(() => import("../Finance/WorksAndExpenses"));
const Vendors = lazy(() => import("../Vendors/Vendors"));
const Visitors = lazy(() => import("../Visitors/Visitors"));
const EmergencyContacts = lazy(() => import("../EmergencyContacts/EmergencyContacts"));
const Notices = lazy(() => import("../Notices/Notices"));
const Polls = lazy(() => import("../Polls/Polls"));
const Documents = lazy(() => import("../Documents/Documents"));
const DailyHelp = lazy(() => import("../DailyHelp/DailyHelp"));
const Maintenance = lazy(() => import("../Maintenance/Maintenance"));
const AmenityBooking = lazy(() => import("../AmenityBooking/AmenityBooking"));
const MoveTracking = lazy(() => import("../MoveTracking/MoveTracking"));
const SecurityGate = lazy(() => import("../SecurityGate/SecurityGate"));
const MyProfile = lazy(() => import("../User/MyProfile"));
const SocialHub = lazy(() => import("../Social/SocialHub"));
const NotificationSettings = lazy(() => import("../Shared/NotificationSettings"));
const ActivityCalendar = lazy(() => import("../Shared/ActivityCalendar"));
const ModuleWizards = lazy(() => import("../Shared/ModuleWizards"));
const Engagement = lazy(() => import("../Engagement/Engagement"));
const StoryWall = lazy(() => import("../Engagement/StoryWall"));
const Offerings = lazy(() => import("../Engagement/Offerings"));
const BuddyGroups = lazy(() => import("../Engagement/BuddyGroups"));
const DailyPuzzles = lazy(() => import("../Engagement/DailyPuzzles"));
const Games = lazy(() => import("../Engagement/Games"));
const Contribute = lazy(() => import("../Engagement/Contribute"));
const SkillsBoard = lazy(() => import("../Engagement/SkillsBoard"));
const BillingInputs = lazy(() => import("../Pay/BillingInputs"));

const LazyFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

const Lazy = ({ children }) => <Suspense fallback={<LazyFallback />}>{children}</Suspense>;

const community = [
  {
    path: "/community",
    element: <AppLayout><Lazy><Community /></Lazy></AppLayout>,
  },
  {
    path: "/people",
    element: <AppLayout><Lazy><People /></Lazy></AppLayout>,
  },
  {
    path: "/people/cross-community",
    element: <AppLayout><Lazy><CrossCommunity /></Lazy></AppLayout>,
  },
  {
    path: "/works",
    element: <AppLayout><Lazy><WorksAndExpenses /></Lazy></AppLayout>,
  },
  {
    path: "/account",
    element: <AppLayout><Lazy><Pay /></Lazy></AppLayout>,
  },
  {
    path: "/account-form",
    element: <AppLayout><Lazy><AccountForm /></Lazy></AppLayout>,
  },
  {
    path: "/account-details",
    element: <AppLayout><Lazy><AccountDetails /></Lazy></AppLayout>,
  },
  {
    path: "/fund-raise-details",
    element: <AppLayout><Lazy><FundRaiseDetails /></Lazy></AppLayout>,
  },
  {
    path: "/dues",
    element: <AppLayout><Lazy><Dues /></Lazy></AppLayout>,
  },
  {
    path: "/review-payments",
    element: <AppLayout><Lazy><ReviewPayments /></Lazy></AppLayout>,
  },
  {
    path: "/billing-inputs",
    element: <AppLayout><Lazy><BillingInputs /></Lazy></AppLayout>,
  },
  {
    path: "/association",
    element: <AppLayout><Lazy><Body /></Lazy></AppLayout>,
  },
];

const AppRouter = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout><Lazy><AppHome /></Lazy></AppLayout>,
  },
  ...community,
  {
    path: "/chats",
    element: <AppLayout><Lazy><Chat /></Lazy></AppLayout>,
  },
  {
    path: "/discussion",
    element: <AppLayout><Lazy><Discussion /></Lazy></AppLayout>,
  },
  {
    path: "/invitations",
    element: <AppLayout><Lazy><InvitationContainer /></Lazy></AppLayout>,
  },
  {
    path: "/invite",
    element: <AppLayout><Lazy><Invite /></Lazy></AppLayout>,
  },
  {
    path: "/vendors",
    element: <AppLayout><Lazy><Vendors /></Lazy></AppLayout>,
  },
  {
    path: "/visitors",
    element: <AppLayout><Lazy><Visitors /></Lazy></AppLayout>,
  },
  {
    path: "/emergency",
    element: <AppLayout><Lazy><EmergencyContacts /></Lazy></AppLayout>,
  },
  {
    path: "/notices",
    element: <AppLayout><Lazy><Notices /></Lazy></AppLayout>,
  },
  {
    path: "/polls",
    element: <AppLayout><Lazy><Polls /></Lazy></AppLayout>,
  },
  {
    path: "/documents",
    element: <AppLayout><Lazy><Documents /></Lazy></AppLayout>,
  },
  {
    path: "/daily-help",
    element: <AppLayout><Lazy><DailyHelp /></Lazy></AppLayout>,
  },
  {
    path: "/maintenance",
    element: <AppLayout><Lazy><Maintenance /></Lazy></AppLayout>,
  },
  {
    path: "/amenities",
    element: <AppLayout><Lazy><AmenityBooking /></Lazy></AppLayout>,
  },
  {
    path: "/move-tracking",
    element: <AppLayout><Lazy><MoveTracking /></Lazy></AppLayout>,
  },
  {
    path: "/social",
    element: <AppLayout><Lazy><SocialHub /></Lazy></AppLayout>,
  },
  {
    path: "/profile",
    element: <AppLayout><Lazy><MyProfile /></Lazy></AppLayout>,
  },
  {
    path: "/notification-settings",
    element: <AppLayout><Lazy><NotificationSettings /></Lazy></AppLayout>,
  },
  {
    path: "/calendar",
    element: <AppLayout><Lazy><ActivityCalendar /></Lazy></AppLayout>,
  },
  {
    path: "/wizards",
    element: <AppLayout><Lazy><ModuleWizards /></Lazy></AppLayout>,
  },
  {
    path: "/engagement",
    element: <AppLayout><Lazy><Engagement /></Lazy></AppLayout>,
  },
  {
    path: "/engagement/stories",
    element: <AppLayout><Lazy><StoryWall /></Lazy></AppLayout>,
  },
  {
    path: "/engagement/offerings",
    element: <AppLayout><Lazy><Offerings /></Lazy></AppLayout>,
  },
  {
    path: "/engagement/buddy-groups",
    element: <AppLayout><Lazy><BuddyGroups /></Lazy></AppLayout>,
  },
  {
    path: "/engagement/puzzles",
    element: <AppLayout><Lazy><DailyPuzzles /></Lazy></AppLayout>,
  },
  {
    path: "/engagement/games",
    element: <AppLayout><Lazy><Games /></Lazy></AppLayout>,
  },
  {
    path: "/engagement/contribute",
    element: <AppLayout><Lazy><Contribute /></Lazy></AppLayout>,
  },
  {
    path: "/engagement/skills",
    element: <AppLayout><Lazy><SkillsBoard /></Lazy></AppLayout>,
  },
  {
    path: "/gate",
    element: <Lazy><SecurityGate /></Lazy>,
  },
  {
    path: "/how-to-use",
    element: <Lazy><HowToUse /></Lazy>,
  },
  {
    path: "/demo",
    element: <Lazy><DemoPage /></Lazy>,
  },
  {
    path: "/how-to-use/guide",
    element: <Lazy><FeatureGuide /></Lazy>,
  },
  // Only enable reset-data route on localhost
  ...(window.location.hostname === "localhost"
    ? [{
        path: "/reset-data",
        element: <Lazy><ResetData /></Lazy>,
      }]
    : []),
  {
    path: "/signin",
    element: <Lazy><SignIn /></Lazy>,
  },
  {
    path: "/signup",
    element: <Lazy><Signup /></Lazy>,
  },
  ...issuesRoutes,
]);

export default AppRouter;
